// Globals
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Helpers
import {
  clearResumeSession,
  HANDOFF_STATUS_COPY,
  loadResumeSession,
  mergeHistory,
  saveResumeSession,
  SocketContext,
} from '../../helpers';

// Modules
import { useMessages, useSocketIO } from "@/modules/Socket/hooks";
import { useSettings } from '@/modules/Settings';

// Models
import { HandoffState } from '@/models/chat.model';

// Constants
import { DEBUG_TOOLS_ENABLED, DEFAULT_OPERATOR_LABEL } from '@/constants/common';

type TChatProviderProps = {
  children?: ReactNode;
};

const SocketContainer = ({ children }: TChatProviderProps) => {
  const [isTyping, setIsTyping] = useState(false);
  const [isConversationClosed, setConversationClosed] = useState(false);
  const [isDebugEnabled, setDebugEnabled] = useState(false);
  const { messages, setMessages } = useMessages();
  const { settings } = useSettings();

  // Injects the channel's welcome_message as the very first AI message on a
  // fresh session (empty history). Runs once per mount — guarded by the ref
  // so it never re-fires after the user starts chatting or reconnects.
  const hasInjectedWelcomeRef = useRef(false);

  const handoffStateRef = useRef<HandoffState | null>(null);

  useEffect(() => {
    if (hasInjectedWelcomeRef.current) return;
    hasInjectedWelcomeRef.current = true;
    if (!settings?.welcome_message) return;

    setMessages((prev) => (prev.length === 0 ? [
      {
        message: settings.welcome_message,
        sender: 'AI',
        date: new Date(),
      },
    ] : prev));
  }, [settings, setMessages]);

  const { sendMessage, reconnect } = useSocketIO({
    onConnect: () => {
      console.log('[socket] connected');
    },
    onDisconnect: (meta) => {
      console.log('[socket] disconnected', meta.code, meta.reason);
    },
    onReady: (frame) => {
      setConversationClosed(false);

      const storedBeforeConnect = loadResumeSession();
      const resumed = !!storedBeforeConnect && storedBeforeConnect?.conversationId === frame.conversation_id;

      if (typeof frame.resume_secret === 'string' && frame.resume_secret) {
        saveResumeSession(frame.conversation_id, frame.resume_secret);
      }

      if (!resumed && storedBeforeConnect) {
        handoffStateRef.current = null;
        setMessages((prev) => [
          ...prev,
          {
            message: 'New conversation started.',
            sender: 'SYSTEM',
            tone: 'neutral',
            date: new Date(),
          },
        ]);
      }
    },
    onAssistantMessage: (frame) => {
      setIsTyping(false);
      if (
        !frame.response
        && !frame.cards?.length
        && !frame.suggested_actions?.length
        && !frame.comparison_data
      ) return;
      setMessages((prev) => [
        ...prev,
        {
          message: frame.response,
          sender: 'AI',
          date: new Date(),
          cards: frame.cards?.length ? frame.cards : undefined,
          comparisonData: frame.comparison_data ?? undefined,
          suggestedActions: frame.suggested_actions?.length ? frame.suggested_actions : undefined,
        },
      ]);
    },
    onHistory: (frame) => {
      setMessages((prev) => mergeHistory(frame, prev));
    },
    onOperatorMessage: (frame) => {
      setIsTyping(false);
      setMessages((prev) => {
        const alreadyExists = prev.some((message) => (
          message.sender === 'OPERATOR'
          && (message.seq === frame.seq || (message.message === frame.text && message.date === frame.sent_at))
        ));
        if (alreadyExists) return prev;

        return [
          ...prev,
          {
            message: frame.text,
            sender: 'OPERATOR',
            label: frame.label || DEFAULT_OPERATOR_LABEL,
            date: frame.sent_at,
            seq: frame.seq,
          },
        ];
      });
    },
    onHandoffStatus: (frame) => {
      handoffStateRef.current = frame.state;
      if (frame.state === HandoffState.ATTENDED) setIsTyping(false);

      const copy = HANDOFF_STATUS_COPY[frame.state];
      if (!copy) return;

      setMessages((prev) => [
        ...prev,
        {
          message: copy.text,
          sender: 'SYSTEM',
          tone: copy.tone,
          date: new Date(),
        },
      ]);
    },
    onErrorFrame: (frame) => {
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          message: `⚠️ AI error: ${frame.reason}`,
          sender: 'AI',
          tone: 'danger',
          date: new Date(),
        },
      ]);
    },
    onClosed: (frame) => {
      setIsTyping(false);
      setConversationClosed(true);
      clearResumeSession();
      handoffStateRef.current = null;
      setMessages((prev) => [
        ...prev,
        {
          message: frame.reason,
          sender: 'SYSTEM',
          tone: 'neutral',
          date: new Date(),
        },
      ]);
    },
    onError: (error) => {
      console.error('[socket] general error:', error);
    },
  });

  const handleSendMessage = useCallback((message: string) => {
    if (!message.trim()) return;
    setMessages((prev) => [
      ...prev,
      {
        message,
        sender: 'USER',
        date: new Date(),
      },
    ]);
    if (handoffStateRef.current !== HandoffState.ATTENDED) setIsTyping(true);

    sendMessage(message, undefined, DEBUG_TOOLS_ENABLED && isDebugEnabled);
  }, [setMessages, sendMessage, isDebugEnabled]);

  // Clears local chat history and forces a fresh WebSocket connection, so the
  // backend issues a brand-new conversation_id via the next READY frame.
  const restartSession = useCallback(() => {
    setIsTyping(false);
    setConversationClosed(false);
    handoffStateRef.current = null;
    setMessages([]);
    clearResumeSession();
    reconnect();
  }, [setMessages, reconnect]);

  const value = useMemo(() => ({
    messages,
    isTyping,
    isConversationClosed,
    sendMessage: handleSendMessage,
    setMessages,
    restartSession,
    isDebugEnabled,
    setDebugEnabled,
  }), [
    messages,
    isTyping,
    isConversationClosed,
    handleSendMessage,
    setMessages,
    restartSession,
    isDebugEnabled,
  ]);

  return (
      <SocketContext.Provider value={value}>
        {children}
      </SocketContext.Provider>
  );
};

export default SocketContainer;
